import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from '../../utils/translations';
import { createShoppingList } from '../../api/shoppingList';
import { useNotifications } from '../../hooks/useNotifications';

interface AddListPanelProps {
  open: boolean;
  onClose: () => void;
  onListCreated: (listId: string) => void;
}

const AddListPanel: React.FC<AddListPanelProps> = ({ open, onClose, onListCreated }) => {
  const getDefaultListName = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `Lista na ${day}-${month}-${year}`;
  };

  const [name, setName] = useState<string>(getDefaultListName());
  const [saving, setSaving] = useState<boolean>(false);

  const { showError } = useNotifications();

  useEffect(() => {
    if (open) {
      setName(getDefaultListName());
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      showError('Nazwa listy jest wymagana', 'Błąd walidacji');
      return;
    }

    try {
      setSaving(true);
      const response = await createShoppingList({ name: name.trim() });
      setName(getDefaultListName());
      onListCreated(response.id);
      // Don't call handleClose here - let the parent handle navigation
    } catch (error) {
      showError(
        error instanceof Error ? error.message : 'Nie udało się utworzyć listy',
        'Błąd'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setName(getDefaultListName());
    onClose();
  };

  return (
    <Drawer anchor="bottom" open={open} onClose={handleClose}>
      <Box sx={{ padding: 3, minHeight: '250px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <Typography variant="h6">{t('shoppingLists.addList')}</Typography>
          <IconButton onClick={handleClose} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label={t('shoppingLists.listName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={saving}
            autoFocus
            sx={{ marginBottom: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
            <Button onClick={handleClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !name.trim()}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? t('common.saving') : t('common.add')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddListPanel;